/**
 * Invoice Reconciliation Page
 * Allows admins to check invoice consistency for events
 */

import { useState } from 'react';
import { functions } from '@/lib/firebase';
import { httpsCallable } from 'firebase/functions';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import Header from '@/components/layout/Header';
import { showSuccess, showError } from '@/lib/swal';
import { Search, AlertTriangle, CheckCircle, Copy, ExternalLink } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface InvoiceIssue {
  invoiceId: string;
  issueType: 'payment_mismatch' | 'status_mismatch' | 'rsvp_mismatch' | 'balance_mismatch';
  severity: 'error' | 'warning';
  message: string;
  details?: Record<string, any>;
}

interface ReconciliationResult {
  success: boolean;
  unitId: string;
  eventId: string;
  invoiceCount: number;
  issueCount: number;
  issues: InvoiceIssue[];
  truncated?: boolean;
}

export default function ReconciliationPage() {
  const [unitId, setUnitId] = useState('');
  const [eventId, setEventId] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ReconciliationResult | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [issueTypeFilter, setIssueTypeFilter] = useState<string>('all');

  const handleReconcile = async () => {
    if (!unitId.trim() || !eventId.trim()) {
      showError('Please enter both Unit ID and Event ID');
      return;
    }

    setLoading(true);
    try {
      const reconcileEventInvoices = httpsCallable<{ unitId: string; eventId: string }, ReconciliationResult>(
        functions,
        'reconcileEventInvoices'
      );

      const response = await reconcileEventInvoices({ unitId: unitId.trim(), eventId: eventId.trim() });
      setResult(response.data);
      showSuccess(`Reconciliation complete! Found ${response.data.issueCount} issue(s) in ${response.data.invoiceCount} invoice(s).`);
    } catch (error: any) {
      console.error('Error reconciling invoices:', error);
      showError(error.message || 'Failed to reconcile invoices');
    } finally {
      setLoading(false);
    }
  };

  const filteredIssues = result?.issues.filter((issue) => {
    const matchesSearch =
      !searchQuery ||
      issue.invoiceId.toLowerCase().includes(searchQuery.toLowerCase()) ||
      issue.message.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesType = issueTypeFilter === 'all' || issue.issueType === issueTypeFilter;

    return matchesSearch && matchesType;
  }) || [];

  const handleCopyInvoiceId = (invoiceId: string) => {
    navigator.clipboard.writeText(invoiceId);
    showSuccess('Invoice ID copied to clipboard');
  };

  const handleOpenInvoice = (invoiceId: string) => {
    window.open(`/invoice-management?search=${encodeURIComponent(invoiceId)}`, '_self');
  };

  const getSeverityBadge = (severity: string) => {
    return severity === 'error' ? (
      <Badge variant="destructive">Error</Badge>
    ) : (
      <Badge variant="outline">Warning</Badge>
    );
  };

  const getIssueTypeBadge = (type: string) => {
    const labels: Record<string, string> = {
      payment_mismatch: 'Payment Mismatch',
      status_mismatch: 'Status Mismatch',
      rsvp_mismatch: 'RSVP Mismatch',
      balance_mismatch: 'Balance Mismatch',
    };
    return <Badge variant="secondary">{labels[type] || type}</Badge>;
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main className="flex-1 px-4 py-8">
        <div className="container mx-auto max-w-7xl">
          <div className="mb-8">
            <h1 className="text-3xl font-bold">Invoice Reconciliation</h1>
            <p className="text-muted-foreground">Check invoice consistency for events</p>
          </div>

          {/* Input Form */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Reconcile Event Invoices</CardTitle>
              <CardDescription>Enter Unit ID and Event ID to check invoice consistency</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-3">
                <div>
                  <Label htmlFor="unitId">Unit ID</Label>
                  <Input
                    id="unitId"
                    value={unitId}
                    onChange={(e) => setUnitId(e.target.value)}
                    placeholder="Enter unit ID"
                  />
                </div>
                <div>
                  <Label htmlFor="eventId">Event ID</Label>
                  <Input
                    id="eventId"
                    value={eventId}
                    onChange={(e) => setEventId(e.target.value)}
                    placeholder="Enter event ID"
                  />
                </div>
                <div className="flex items-end">
                  <Button onClick={handleReconcile} disabled={loading} className="w-full">
                    {loading ? 'Reconciling...' : 'Run Reconciliation'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Results Summary */}
          {result && (
            <>
              <div className="mb-6 grid gap-4 md:grid-cols-3">
                <Card>
                  <CardHeader className="pb-3">
                    <CardDescription>Invoices Checked</CardDescription>
                    <CardTitle className="text-3xl">{result.invoiceCount}</CardTitle>
                  </CardHeader>
                </Card>
                <Card>
                  <CardHeader className="pb-3">
                    <CardDescription>Issues Found</CardDescription>
                    <CardTitle className="text-3xl text-orange-600">{result.issueCount}</CardTitle>
                  </CardHeader>
                </Card>
                <Card>
                  <CardHeader className="pb-3">
                    <CardDescription>Status</CardDescription>
                    <CardTitle className="text-3xl">
                      {result.issueCount === 0 ? (
                        <span className="text-green-600">Clean</span>
                      ) : (
                        <span className="text-orange-600">Issues</span>
                      )}
                    </CardTitle>
                  </CardHeader>
                </Card>
              </div>

              {result.truncated && (
                <Card className="mb-6 border-orange-200 bg-orange-50">
                  <CardContent className="pt-6">
                    <p className="text-sm text-orange-800">
                      ⚠️ Results were truncated to 200 issues. There may be more issues in this event.
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Filters */}
              {result.issueCount > 0 && (
                <Card className="mb-6">
                  <CardContent className="pt-6">
                    <div className="grid gap-4 md:grid-cols-2">
                      <div>
                        <Label htmlFor="search">Search</Label>
                        <div className="relative">
                          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                          <Input
                            id="search"
                            placeholder="Search by invoice ID or message..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-10"
                          />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="issueType">Issue Type</Label>
                        <Select value={issueTypeFilter} onValueChange={setIssueTypeFilter}>
                          <SelectTrigger id="issueType">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Issues</SelectItem>
                            <SelectItem value="payment_mismatch">Payment Mismatch</SelectItem>
                            <SelectItem value="status_mismatch">Status Mismatch</SelectItem>
                            <SelectItem value="rsvp_mismatch">RSVP Mismatch</SelectItem>
                            <SelectItem value="balance_mismatch">Balance Mismatch</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Issues Table */}
              {filteredIssues.length > 0 ? (
                <Card>
                  <CardHeader>
                    <CardTitle>Issues ({filteredIssues.length})</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Severity</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Invoice ID</TableHead>
                          <TableHead>Message</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredIssues.map((issue, index) => (
                          <TableRow key={`${issue.invoiceId}-${index}`}>
                            <TableCell>{getSeverityBadge(issue.severity)}</TableCell>
                            <TableCell>{getIssueTypeBadge(issue.issueType)}</TableCell>
                            <TableCell className="font-mono text-sm">{issue.invoiceId}</TableCell>
                            <TableCell>{issue.message}</TableCell>
                            <TableCell>
                              <div className="flex gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleCopyInvoiceId(issue.invoiceId)}
                                  title="Copy Invoice ID"
                                >
                                  <Copy className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleOpenInvoice(issue.invoiceId)}
                                  title="Open Invoice"
                                >
                                  <ExternalLink className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              ) : result.issueCount === 0 ? (
                <Card>
                  <CardContent className="pt-6 text-center">
                    <CheckCircle className="mx-auto mb-4 h-12 w-12 text-green-600" />
                    <p className="text-lg font-semibold">No issues found!</p>
                    <p className="text-muted-foreground">All invoices are consistent.</p>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="pt-6 text-center">
                    <AlertTriangle className="mx-auto mb-4 h-12 w-12 text-orange-600" />
                    <p className="text-lg font-semibold">No issues match your filters</p>
                    <p className="text-muted-foreground">Try adjusting your search or filter criteria.</p>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
